import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Plus,
  MoreHorizontal,
  Users,
  DollarSign,
  Eye,
  Edit,
  Ban,
  Link2,
  Copy,
  CheckCircle,
  Clock,
  UserPlus,
  Percent,
  Mail,
  Phone,
  User,
  Building2,
  Save,
  KeyRound,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Admin, AdminGroup } from '@/domain/admin/admin.types'
import { AdminService } from '@/domain/admin/admin.service'

interface Affiliate {
  id: string
  code: string
  name: string
  email: string
  phone: string
  level: 'bronze' | 'silver' | 'gold' | 'platinum' | 'vip'
  status: 'active' | 'pending' | 'suspended'
  commissionRate: number
  totalMembers: number
  activeMembers: number
  totalCommission: number
  pendingCommission: number
  totalDeposit: number
  joinDate: string
  lastActive: string
}

const mapAdminStatus = (status: Admin['status']): Affiliate['status'] => {
  if (status === 'Pending') return 'pending'
  if (status === 'Suspend') return 'suspended'
  return 'active'
}

const mapAdminToAffiliate = (admin: Admin): Affiliate => {
  const name = `${admin.firstname ?? ''} ${admin.lastname ?? ''}`.trim()
  return {
    id: admin.id,
    code: admin.username || admin.id,
    name: name || admin.username || admin.email || '-',
    email: admin.email || '-',
    phone: admin.admin_tel || '-',
    level: 'bronze',
    status: mapAdminStatus(admin.status),
    commissionRate: 0,
    totalMembers: 0,
    activeMembers: 0,
    totalCommission: 0,
    pendingCommission: 0,
    totalDeposit: 0,
    joinDate: admin.createdAt || '-',
    lastActive: admin.lastLogin || '-',
  }
}

export function AffiliatesListPage() {
  const { t: _t } = useTranslation()
  void _t // Suppress unused warning - kept for future i18n
  const [searchTerm, setSearchTerm] = useState('')
  const [affiliates, setAffiliates] = useState<Affiliate[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newAffiliate, setNewAffiliate] = useState({
    name: '',
    email: '',
    phone: '',
    lineId: '',
    level: 'bronze' as Affiliate['level'],
    commissionRate: 25,
    bankName: '',
    bankAccount: '',
    bankAccountName: '',
    password: '',
  })

  useEffect(() => {
    const loadPartners = async () => {
      setIsLoading(true)
      try {
        const result = await AdminService.findAll({ page: 1, limit: 200 })
        console.log('[AffiliatesListPage] All admins:', result.admins.length, 
          result.admins.map(a => ({ id: a.id, username: a.username, admin_group: a.admin_group, type: typeof a.admin_group }))
        )
        // Filter เฉพาะ admin ที่เป็น PARTNER (admin_group = 3)
        // ใช้ == แทน === เพื่อรองรับทั้ง string และ number
        const partners = result.admins.filter(
          (admin) => Number(admin.admin_group) === AdminGroup.PARTNER
        )
        console.log('[AffiliatesListPage] Partners found:', partners.length)
        setAffiliates(partners.map(mapAdminToAffiliate))
      } catch (error) {
        console.error('[AffiliatesListPage] loadPartners error:', error)
        setAffiliates([])
      } finally {
        setIsLoading(false)
      }
    }

    loadPartners()
  }, [])

  const filteredAffiliates = useMemo(() => {
    if (!searchTerm) return affiliates
    const term = searchTerm.toLowerCase()
    return affiliates.filter(a =>
      a.name.toLowerCase().includes(term) ||
      a.code.toLowerCase().includes(term) ||
      a.email.toLowerCase().includes(term) ||
      a.phone.includes(term)
    )
  }, [affiliates, searchTerm])

  const stats = useMemo(() => ({
    total: affiliates.length,
    active: affiliates.filter(a => a.status === 'active').length,
    totalMembers: affiliates.reduce((sum, a) => sum + a.totalMembers, 0),
    totalCommission: affiliates.reduce((sum, a) => sum + a.totalCommission, 0),
  }), [affiliates])

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getLevelBadge = (level: Affiliate['level']) => {
    const config = {
      vip: { label: 'VIP', icon: '👑', className: 'bg-purple-500/10 text-purple-500' },
      platinum: { label: 'Platinum', icon: '💎', className: 'bg-cyan-500/10 text-cyan-500' },
      gold: { label: 'Gold', icon: '🥇', className: 'bg-yellow-500/10 text-yellow-500' },
      silver: { label: 'Silver', icon: '🥈', className: 'bg-gray-400/10 text-gray-400' },
      bronze: { label: 'Bronze', icon: '🥉', className: 'bg-orange-600/10 text-orange-600' },
    }
    const { label, icon, className } = config[level]
    return (
      <Badge variant="secondary" className={cn("border-0 gap-1", className)}>
        <span>{icon}</span> {label}
      </Badge>
    )
  }

  const getStatusBadge = (status: Affiliate['status']) => {
    const config = {
      active: { label: 'ใช้งาน', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-win/10 text-win' },
      pending: { label: 'รออนุมัติ', icon: <Clock className="h-3 w-3" />, className: 'bg-warning/10 text-warning' },
      suspended: { label: 'ระงับ', icon: <Ban className="h-3 w-3" />, className: 'bg-destructive/10 text-destructive' },
    }
    const { label, icon, className } = config[status]
    return (
      <Badge variant="secondary" className={cn("border-0 gap-1", className)}>
        {icon} {label}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">จัดการพันธมิตร</h1>
          <p className="text-muted-foreground">บริหารจัดการพันธมิตรและตัวแทน</p>
        </div>
        <Button className="gap-2" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4" />
          เพิ่มพันธมิตร
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">พันธมิตรทั้งหมด</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ใช้งาน</p>
                <p className="text-xl font-bold text-win">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <UserPlus className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกทั้งหมด</p>
                <p className="text-xl font-bold">{formatNumber(stats.totalMembers)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <DollarSign className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คอมมิชชั่นรวม</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalCommission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา รหัส, ชื่อ, อีเมล, โทรศัพท์..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-y">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">พันธมิตร</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">ระดับ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">คอมฯ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">สมาชิก</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ยอดฝากรวม</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">คอมมิชชั่น</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr className="border-b">
                    <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={8}>
                      กำลังโหลดข้อมูลพันธมิตร...
                    </td>
                  </tr>
                ) : filteredAffiliates.length === 0 ? (
                  <tr className="border-b">
                    <td className="px-4 py-6 text-center text-sm text-muted-foreground" colSpan={8}>
                      ไม่พบข้อมูลพันธมิตร
                    </td>
                  </tr>
                ) : (
                  filteredAffiliates.map((affiliate) => (
                    <tr key={affiliate.id} className="border-b hover:bg-muted/30">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
                          {affiliate.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">{affiliate.name}</p>
                          <p className="text-xs text-muted-foreground">{affiliate.code} • {affiliate.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getLevelBadge(affiliate.level)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getStatusBadge(affiliate.status)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                        {affiliate.commissionRate}%
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div>
                        <p className="font-medium">{formatNumber(affiliate.totalMembers)}</p>
                        <p className="text-xs text-win">Active: {formatNumber(affiliate.activeMembers)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium">{formatCurrency(affiliate.totalDeposit)}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div>
                        <p className="font-medium text-win">{formatCurrency(affiliate.totalCommission)}</p>
                        {affiliate.pendingCommission > 0 && (
                          <p className="text-xs text-warning">รอจ่าย: {formatCurrency(affiliate.pendingCommission)}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="gap-2" onClick={() => setSelectedAffiliate(affiliate)}>
                            <Eye className="h-4 w-4" /> ดูรายละเอียด
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-2">
                            <Edit className="h-4 w-4" /> แก้ไข
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-2">
                            <Link2 className="h-4 w-4" /> ดูลิงก์
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="gap-2 text-destructive">
                            <Ban className="h-4 w-4" /> ระงับบัญชี
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedAffiliate} onOpenChange={() => setSelectedAffiliate(null)}>
        <DialogContent className="max-w-2xl">
          {selectedAffiliate && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-xl text-primary">
                    {selectedAffiliate.name.charAt(0)}
                  </div>
                  <div>
                    <p>{selectedAffiliate.name}</p>
                    <p className="text-sm font-normal text-muted-foreground">{selectedAffiliate.code}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">ระดับ</p>
                    {getLevelBadge(selectedAffiliate.level)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">สถานะ</p>
                    {getStatusBadge(selectedAffiliate.status)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">อีเมล</p>
                    <p className="font-medium">{selectedAffiliate.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">โทรศัพท์</p>
                    <p className="font-medium">{selectedAffiliate.phone}</p>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 pt-4 border-t">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold text-primary">{selectedAffiliate.commissionRate}%</p>
                    <p className="text-xs text-muted-foreground">อัตราคอมฯ</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{formatNumber(selectedAffiliate.totalMembers)}</p>
                    <p className="text-xs text-muted-foreground">สมาชิก</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold text-win">{formatNumber(selectedAffiliate.activeMembers)}</p>
                    <p className="text-xs text-muted-foreground">Active</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{formatCurrency(selectedAffiliate.totalDeposit)}</p>
                    <p className="text-xs text-muted-foreground">ยอดฝาก</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="p-4 rounded-lg bg-win/5 border border-win/20">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-win" />
                      <span className="text-sm text-win">คอมมิชชั่นรวม</span>
                    </div>
                    <p className="text-2xl font-bold text-win">{formatCurrency(selectedAffiliate.totalCommission)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-warning/5 border border-warning/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-5 w-5 text-warning" />
                      <span className="text-sm text-warning">รอจ่าย</span>
                    </div>
                    <p className="text-2xl font-bold text-warning">{formatCurrency(selectedAffiliate.pendingCommission)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                  <Link2 className="h-4 w-4 text-muted-foreground" />
                  <code className="text-xs flex-1">https://example.com/ref/{selectedAffiliate.code}</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Affiliate Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              เพิ่มพันธมิตรใหม่
            </DialogTitle>
            <DialogDescription>
              กรอกข้อมูลเพื่อสร้างบัญชีพันธมิตรใหม่
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Personal Info */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                ข้อมูลส่วนตัว
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">ชื่อ-นามสกุล <span className="text-destructive">*</span></Label>
                  <Input
                    id="name"
                    placeholder="กรอกชื่อ-นามสกุล"
                    value={newAffiliate.name}
                    onChange={(e) => setNewAffiliate({...newAffiliate, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">อีเมล <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="email@example.com"
                      className="pl-10"
                      value={newAffiliate.email}
                      onChange={(e) => setNewAffiliate({...newAffiliate, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">เบอร์โทรศัพท์ <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      placeholder="08X-XXX-XXXX"
                      className="pl-10"
                      value={newAffiliate.phone}
                      onChange={(e) => setNewAffiliate({...newAffiliate, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lineId">LINE ID</Label>
                  <Input
                    id="lineId"
                    placeholder="@lineid"
                    value={newAffiliate.lineId}
                    onChange={(e) => setNewAffiliate({...newAffiliate, lineId: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Commission Settings */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Percent className="h-4 w-4" />
                ตั้งค่าคอมมิชชั่น
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="level">ระดับพันธมิตร</Label>
                  <Select 
                    value={newAffiliate.level}
                    onValueChange={(value) => {
                      const rates = { bronze: 25, silver: 30, gold: 35, platinum: 40, vip: 45 }
                      setNewAffiliate({
                        ...newAffiliate, 
                        level: value as Affiliate['level'],
                        commissionRate: rates[value as keyof typeof rates]
                      })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกระดับ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bronze">🥉 Bronze (25%)</SelectItem>
                      <SelectItem value="silver">🥈 Silver (30%)</SelectItem>
                      <SelectItem value="gold">🥇 Gold (35%)</SelectItem>
                      <SelectItem value="platinum">💎 Platinum (40%)</SelectItem>
                      <SelectItem value="vip">👑 VIP (45%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="commission">อัตราคอมมิชชั่น (%)</Label>
                  <Input
                    id="commission"
                    type="number"
                    min="0"
                    max="100"
                    value={newAffiliate.commissionRate}
                    onChange={(e) => setNewAffiliate({...newAffiliate, commissionRate: Number(e.target.value)})}
                  />
                </div>
              </div>
              <div className="mt-3 p-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
                💡 อัตราคอมมิชชั่นจะคำนวณจากยอดฝากครั้งแรกของสมาชิกที่แนะนำมา
              </div>
            </div>

            {/* Bank Account */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                บัญชีธนาคาร (สำหรับรับเงินคอมมิชชั่น)
              </h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="bankName">ธนาคาร <span className="text-destructive">*</span></Label>
                  <Select 
                    value={newAffiliate.bankName}
                    onValueChange={(value) => setNewAffiliate({...newAffiliate, bankName: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกธนาคาร" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kbank">กสิกรไทย</SelectItem>
                      <SelectItem value="scb">ไทยพาณิชย์</SelectItem>
                      <SelectItem value="bbl">กรุงเทพ</SelectItem>
                      <SelectItem value="ktb">กรุงไทย</SelectItem>
                      <SelectItem value="bay">กรุงศรี</SelectItem>
                      <SelectItem value="tmb">ทหารไทยธนชาต</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bankAccount">เลขบัญชี <span className="text-destructive">*</span></Label>
                  <Input
                    id="bankAccount"
                    placeholder="XXX-X-XXXXX-X"
                    value={newAffiliate.bankAccount}
                    onChange={(e) => setNewAffiliate({...newAffiliate, bankAccount: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bankAccountName">ชื่อบัญชี <span className="text-destructive">*</span></Label>
                  <Input
                    id="bankAccountName"
                    placeholder="ชื่อเจ้าของบัญชี"
                    value={newAffiliate.bankAccountName}
                    onChange={(e) => setNewAffiliate({...newAffiliate, bankAccountName: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Login Credentials */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                ข้อมูลเข้าสู่ระบบ
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>อีเมล (ใช้เข้าสู่ระบบ)</Label>
                  <Input
                    value={newAffiliate.email}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">รหัสผ่าน <span className="text-destructive">*</span></Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="กรอกรหัสผ่าน (อย่างน้อย 6 ตัว)"
                    value={newAffiliate.password}
                    onChange={(e) => setNewAffiliate({...newAffiliate, password: e.target.value})}
                  />
                </div>
              </div>
              <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm">
                <p className="text-blue-500 font-medium">📧 ระบบจะส่งอีเมลแจ้งข้อมูลเข้าสู่ระบบไปยังอีเมลของพันธมิตรอัตโนมัติ</p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              ยกเลิก
            </Button>
            <Button 
              className="gap-2"
              disabled={!newAffiliate.name || !newAffiliate.email || !newAffiliate.phone || !newAffiliate.password}
              onClick={() => {
                // Save logic here
                console.log('Creating affiliate:', newAffiliate)
                setShowCreateDialog(false)
                // Reset form
                setNewAffiliate({
                  name: '',
                  email: '',
                  phone: '',
                  lineId: '',
                  level: 'bronze',
                  commissionRate: 25,
                  bankName: '',
                  bankAccount: '',
                  bankAccountName: '',
                  password: '',
                })
              }}
            >
              <Save className="h-4 w-4" />
              สร้างพันธมิตร
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

